import { SupportedLocales, isValidLocale } from "@wac-web/utils/localization";

/**
 * Route definitions for Fastshift Portal
 * This is a subset of wac-routes.ts focused only on fastshift routes
 */
export type FastshiftRouteWithVariant =
    | {
          locale: SupportedLocales;
          featureFlags: Record<string, boolean>;
          product?: string;
      } & (
          | { path: "fastshift"; variant: undefined; search: string }
          | { path: `fastshift/${string}`; variant: undefined; search: string }
      );

export const getFeatureFlagValues = (params: string[]) => {
    // set defaults for feature flags
    const featureFlags = {
        enableBundlesAfterSignup: false,
        enableCutSignUpStepsInHalf: false,
        provisionSecondPartyApps: false
    };
    params[0] && (params[0] = params[0].replace(/^\?(.+)/, "$1"));
    params.forEach((param) => {
        const [key, value] = param.split("=");
        switch (key) {
            case "provisionSecondPartyApps":
                if (value === "enableProvisionSecondPartyApps") {
                    featureFlags.provisionSecondPartyApps = true;
                }
                break;
        }
    });
    return featureFlags;
};

/**
 * Parse the browser location into a structured route object
 *
 * @param location - Browser Location object
 * @returns Parsed route with locale, path, variant, and additional metadata
 *
 * @example
 * const route = parseRoute(window.location);
 * // Returns: { path: "fastshift", locale: "en", variant: undefined, ... }
 */

export const parseRoute = (
    location: Location
): FastshiftRouteWithVariant & {
    locale: SupportedLocales;
    featureFlags: object;
} => {
    // remove all leading slashes (server had 2 leading slashes, client has 1)
    const noSlashPrefixPath = location.pathname.replace(/^\/+/, "");
    if (noSlashPrefixPath === "index.html" || noSlashPrefixPath === "") {
        // case for the root/home page - redirect to fastshift
        return {
            locale: "en",
            path: "fastshift",
            variant: undefined,
            search: "",
            featureFlags: {}
        };
    }

    // check if the route has a locale prefix
    const potentialLocale = noSlashPrefixPath.split("/")[0] || "";
    const localeIsValid = isValidLocale(potentialLocale);

    // if there is a locale prefix, set the locale variable and remove the prefix from the path
    // otherwise, set the locale to the default (en) and leave the path as is
    const locale = localeIsValid ? potentialLocale : "en";
    const cleanedPath = (
        localeIsValid
            ? noSlashPrefixPath.slice(locale.length + 1)
            : noSlashPrefixPath
    ) as FastshiftRouteWithVariant["path"];

    const params = location.search.split("&");

    // Extract variant from query params (e.g., ?cmsVariant=some-variant)
    const cmsVariantValue = params
        .filter((param) => param.includes("cmsVariant"))[0]
        ?.split("=")[1] as FastshiftRouteWithVariant["variant"];

    const featureFlags = getFeatureFlagValues(params);

    return {
        path: cleanedPath,
        variant: cmsVariantValue,
        locale,
        search: location.search,
        featureFlags
    } as FastshiftRouteWithVariant & {
        locale: SupportedLocales;
        featureFlags: object;
    };
};
